import React from 'react';
import { Accept } from 'react-dropzone';
import WarningIcon from '@mui/icons-material/Warning';
import LzXCsvSvgIcon from '@lz/components/commons/svg-icons/csv';
import LzXHtmlSvgIcon from '@lz/components/commons/svg-icons/html';
import LzXMsExcelSvgIcon from '@lz/components/commons/svg-icons/ms-excel';
import LzXPdfSvgIcon from '@lz/components/commons/svg-icons/pdf';
import LzXTxtSvgIcon from '@lz/components/commons/svg-icons/txt';
import LzMOption from './Option';
import LzMBase, { LzNBase } from './Base';
import { lzFIsArray } from '@lz/services/Type';

/**
 * Media file Types to be defined here.
 * NOTE: if adding another type, do add corresponding ACCEPT_xxx and also edit getFileTypeOptions() below.
 */
export type LzTMediaFileType = 'image' | 'document';

export type LzTMediaContentType =
  | 'text/*'
  | 'text/csv'
  | 'image/*'
  | 'application/json'
  | 'application/pdf'
  | 'audio/*'
  | 'video/*';

export interface LzNMedia extends LzNBase {
  category: string;
  file_name: string;
  file_size: number;
  file_path: string;
  file_preview_path: string;
  file_type: LzTMediaFileType;
  content_type: LzTMediaContentType;
  is_private: boolean;
}

class LzMMedia extends LzMBase implements LzNMedia {
  category: string;
  file_name: string;
  file_size: number;
  file_path: string;
  file_preview_path: string;
  file_type: LzTMediaFileType;
  content_type: LzTMediaContentType;
  is_private: boolean;

  constructor(model?: LzNMedia) {
    super(model);

    this.category = model?.category || '';
    this.file_name = model?.file_name || '';
    this.file_size = +(model?.file_size || 0);
    this.file_path = model?.file_path || '';
    this.file_preview_path = model?.file_preview_path || '';
    this.file_type = model?.file_type || 'image';
    this.content_type = model?.content_type || 'image/*';
    this.is_private = !!model?.is_private;
  }

  static ACCEPT_DOCUMENTS: any = [
    'text/*',
    'image/*',
    'application/pdf',
    'application/json',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  static ACCEPT_IMAGES: any = ['image/*'];

  static CONTENT_TYPE_ICONS: any = {
    '': <WarningIcon style={{ width: '100%', height: '100%' }} />,
    'application/pdf': (
      <LzXPdfSvgIcon style={{ width: '100%', height: '100%' }} />
    ),
    'application/vnd.ms-excel': (
      <LzXMsExcelSvgIcon style={{ width: '100%', height: '100%' }} />
    ),
    'text/csv': <LzXCsvSvgIcon style={{ width: '100%', height: '100%' }} />,
    'text/plain': <LzXTxtSvgIcon style={{ width: '100%', height: '100%' }} />,
    'text/html': <LzXHtmlSvgIcon style={{ width: '100%', height: '100%' }} />,
  };

  static getImageIconByFile(file: any): any {
    // If file object given is not readable or type not existing
    if (!file && typeof file.name === 'undefined') {
      return LzMMedia.CONTENT_TYPE_ICONS[''];
    }

    // If file type is empty (in many cases, file type is not recognized even though it is a common file type)
    if ('' === file.type) {
      if (file.name.indexOf('.csv') > 0) {
        return LzMMedia.CONTENT_TYPE_ICONS['text/csv'];
      }
    }

    // If file is an image
    if (LzMMedia.isImageByContentType(file.type)) {
      return LzMMedia.getImageUrlFromFile(file);
    }

    // If type is specified and its not an image then it comes here, so return icon by type
    return LzMMedia.getIconByContentType(file.type);
  }

  static isImageByContentType(contentType: string): boolean {
    return 0 === contentType.indexOf('image/');
  }

  static getIconByContentType(contentType: string): any {
    return (
      LzMMedia.CONTENT_TYPE_ICONS[contentType] ||
      LzMMedia.CONTENT_TYPE_ICONS['']
    );
  }

  static getImageUrlFromFile(file: any): string {
    return URL.createObjectURL(file);
  }

  static getContentTypeFromParams(
    contentType?: string,
    fileType?: LzTMediaFileType
  ): string | Array<string> {
    if (contentType && contentType !== '') {
      return contentType;
    }

    return fileType === 'image'
      ? LzMMedia.ACCEPT_IMAGES
      : LzMMedia.ACCEPT_DOCUMENTS;
  }

  static getFileTypeOptions(): Array<LzMOption> {
    return [
      new LzMOption({ value: 'image', text: 'Image' }),
      new LzMOption({ value: 'document', text: 'Document' }),
    ];
  }

  static getDropzoneAcceptParam(accept: string | string[] | undefined): Accept {
    const out: Accept = {};

    if (typeof accept === 'string') {
      out[accept] = [];
    }

    if (typeof accept !== 'undefined' && lzFIsArray(accept)) {
      if (accept.length > 0) {
        for (const a of accept) {
          out[a] = [];
        }
      }
    }

    return out;
  }

  get isImage(): boolean {
    return this.file_type === 'image';
  }

  get contentTypeIcon(): any {
    return LzMMedia.getIconByContentType(this.content_type);
  }
}

export default LzMMedia;
